import React, { useEffect, useState } from 'react';
import {
    Container, Grid, Card, Typography, Button,
    Box, ThemeProvider, createTheme, CircularProgress
} from '@mui/material';
import { useNavigate, Link } from 'react-router-dom';
import NavbarMain from '../components/NavbarMain';
import Carousel from '../components/Carousel';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import InstagramIcon from '@mui/icons-material/Instagram';
import LaunchIcon from '@mui/icons-material/Launch';
import CodeIcon from '@mui/icons-material/Code';
import { contactInfoAPI } from '../services/api';
import Footer from "../components/Footer";
import SEO from '../components/SEO';

const HomePage = () => {
    const navigate = useNavigate();
    const [contactInfo, setContactInfo] = useState(null);
    const [contactLoading, setContactLoading] = useState(true);

    useEffect(() => {
        document.body.style.backgroundColor = '#ffffff';
        document.body.style.color = '#000000';
    }, []);

    useEffect(() => {
        const fetchContactInfo = async () => {
            try {
                setContactLoading(true);
                const data = await contactInfoAPI.get();
                setContactInfo(data);
            } catch (error) {
                console.error('Error fetching contact info:', error);
                setContactInfo({
                    phone: '+389 70 000 000',
                    emailPrimary: 'info@bodylab.mk',
                    linkedinUrl: 'https://linkedin.com',
                    instagramUrl: 'https://instagram.com',
                    facebookUrl: 'https://instagram.com', // Default за вториот инстаграм
                    workingHours: 'Пон-Пет: 08:00-22:00'
                });
            } finally {
                setContactLoading(false);
            }
        };
        fetchContactInfo();
    }, []);

    const lightTheme = createTheme({
        palette: {
            mode: 'light',
            primary: { main: '#ff7eb9' },
        },
    });

    return (
        <ThemeProvider theme={lightTheme}>
            <SEO
                title="BodyLab by Anamarija - Професионални Фитнес Програми и Диети"
                description="Персонализирани фитнес програми, диети и тренинзи за жени во Македонија. Постигни ги своите цели со BodyLab by Anamarija."
                url="/"
            />

            <Box sx={{ backgroundColor: '#ffffff', minHeight: '100vh' }}>
                <NavbarMain />

                <Container maxWidth="lg" sx={{ py: 4, mt: 2 }}>

                    {/* Carousel Section */}
                    <Box sx={{ mb: 8 }}>
                        <Carousel />
                    </Box>

                    {/* Главниот наслов (H1) */}
                    <Typography
                        variant="h1"
                        sx={{
                            fontWeight: 900,
                            background: 'linear-gradient(45deg, #ff7eb9, #AB47BC)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            fontSize: { xs: '2.5rem', md: '4rem' },
                            mb: 4,
                            textAlign: 'center'
                        }}
                    >
                        BodyLab by Anamarija
                    </Typography>

                    {/* Текстот под насловот */}
                    <Typography
                        variant="h2"
                        sx={{
                            color: '#004aad',
                            fontWeight: 500,
                            maxWidth: 700,
                            mx: 'auto',
                            fontSize: { xs: '1.1rem', md: '1.5rem' },
                            textAlign: 'center',
                            lineHeight: 1.4
                        }}
                    >
                        Вашиот партнер за професионални фитнес програми и персонализирани диети во Македонија.
                    </Typography>

                    {/* ===== Услуги Section ===== */}
                    <Box sx={{ py: 4 }}>
                        <Typography variant="h2" align="center" sx={{ color: '#ff7eb9', fontWeight: 'bold', mb: 6, fontSize: '2.2rem' }}>
                            Нашите Професионални Услуги
                        </Typography>

                        <Grid container spacing={4} justifyContent="center" alignItems="stretch">
                            <Grid item xs={12} md={5} sx={{ display: 'flex' }}>
                                <Card sx={{
                                    width: '100%',
                                    borderRadius: 5, border: '2px solid #ff7eb940', textAlign: 'center', p: 4,
                                    transition: '0.3s', '&:hover': { transform: 'translateY(-5px)', boxShadow: '0 10px 30px rgba(255,126,185,0.2)' }
                                }}>
                                    <FitnessCenterIcon sx={{ fontSize: 60, color: '#ff7eb9', mb: 2 }} />
                                    <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#ff7eb9', mb: 2, fontSize: '1.8rem' }}>
                                        Програми за тренинг
                                    </Typography>
                                    <Typography sx={{ color: '#666', mb: 4 }}>
                                        Откријте ги нашите персонализирани тренинг програми за жени. Тренинзи за почетници и напредни со детални упатства.
                                    </Typography>
                                    <Button variant="contained" fullWidth sx={{ bgcolor: '#ff7eb9', color: 'white', fontWeight: 'bold', py: 1.5 }} onClick={() => navigate('/programs')}>
                                        Истражете
                                    </Button>
                                </Card>
                            </Grid>

                            <Grid item xs={12} md={5} sx={{ display: 'flex' }}>
                                <Card sx={{
                                    width: '100%',
                                    borderRadius: 5, border: '2px solid #4dabf740', textAlign: 'center', p: 4,
                                    transition: '0.3s', '&:hover': { transform: 'translateY(-5px)', boxShadow: '0 10px 30px rgba(77,171,247,0.2)' }
                                }}>
                                    <RestaurantIcon sx={{ fontSize: 60, color: '#4dabf7', mb: 2 }} />
                                    <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#4dabf7', mb: 2, fontSize: '1.8rem' }}>
                                        Персонализирана исхрана
                                    </Typography>
                                    <Typography sx={{ color: '#666', mb: 4 }}>
                                        Персонализирана исхрана за брзи и трајни резултати.
                                    </Typography>
                                    <Button variant="contained" fullWidth sx={{ bgcolor: '#4dabf7', color: 'white', fontWeight: 'bold', py: 1.5 }} onClick={() => navigate('/diets')}>
                                        Истражете
                                    </Button>
                                </Card>
                            </Grid>
                        </Grid>
                    </Box>

                    {/* SEO Content Section */}
                    <Box sx={{ py: 8, px: { xs: 3, md: 8 }, bgcolor: '#fdf2f7', borderRadius: 8, my: 8 }}>
                        <Typography variant="h3" sx={{ color: '#0d47a1', fontWeight: 'bold', mb: 3, fontSize: '1.8rem' }}>
                            Зошто да го изберете BodyLab за вашето фитнес патување?
                        </Typography>

                        <Typography variant="body1" sx={{ color: '#333', lineHeight: 1.8, mb: 2, textAlign: 'justify' }}>
                            На овој веб сајт ќе пронајдеш комплетно решение за подобрување на твојата физичка форма и здрав начин на живот.
                            Станува збор за структуриран систем што ти помага да ја подобриш формата, здравјето и самодовербата.
                        </Typography>

                        <Typography variant="body1" sx={{ color: '#333', lineHeight: 1.8, mb: 2, textAlign: 'justify' }}>
                            Нашите <Link to="/programs" style={{ color: '#ff7eb9', fontWeight: 'bold', textDecoration: 'none' }}>тренинг програми</Link> се
                            внимателно дизајнирани за различни категории – без разлика дали си почетник или веќе имаш искуство.
                            Прилагодени се и за домашни услови и за теретана. Секој план е јасен, практичен и лесен за следење, со фокус на напредок и континуитет.
                        </Typography>

                        <Typography variant="body1" sx={{ color: '#333', lineHeight: 1.8, mb: 2, textAlign: 'justify' }}>
                            Покрај тоа, нудиме <Link to="/diets" style={{ color: '#4dabf7', fontWeight: 'bold', textDecoration: 'none' }}>персонализирана исхрана</Link> изработена
                            според твоите индивидуални потреби. Доволно е да ги внесеш потребните податоци, а во рок од 24 часа ќе добиеш детална диета што одговара на твоите цели и начин на живот.
                        </Typography>

                        <Typography variant="body1" sx={{ color: '#333', lineHeight: 1.8, textAlign: 'justify' }}>
                            Ова не е универзален план, туку е пристап што се прилагодува на тебе. Започни денес и вложи во верзијата од себе што сакаш да ја постигнеш,
                            а од мене добиваш и постојано следење на твојот напредок, континуирана поддршка и директна комуникација.
                        </Typography>
                    </Box>

                    {/* ===== CONTACT SECTION ===== */}
                    <Box id="contact" sx={{ py: 10 }}>
                        <Typography variant="h2" align="center" sx={{
                            color: '#0d47a1', fontWeight: 900, mb: 2,
                            fontSize: { xs: '2.5rem', md: '4rem' }
                        }}>
                            Ајде да <span style={{ color: '#ff7eb9' }}>започнеме</span>
                        </Typography>
                        <Typography variant="h6" align="center" sx={{
                            mb: 8, color: '#0d47a1', fontWeight: 600, maxWidth: 700, mx: 'auto'
                        }}>
                            Започнете ја вашата трансформација денес. Контактирајте не сега!
                        </Typography>

                        {contactLoading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                <CircularProgress sx={{ color: '#ff7eb9' }} />
                            </Box>
                        ) : (
                            <Grid container spacing={4} justifyContent="center" alignItems="stretch">
                                <Grid item xs={12} sm={6} md={4} sx={{ display: 'flex' }}>
                                    <Box sx={{
                                        p: 4, width: '100%', bgcolor: '#ffffff', borderRadius: 6, textAlign: 'center',
                                        boxShadow: '0 20px 40px rgba(13, 71, 161, 0.1)', border: '1px solid #0d47a120',
                                        transition: '0.3s', '&:hover': { transform: 'translateY(-10px)', borderColor: '#0d47a1' }
                                    }}>
                                        <Box sx={{ width: 80, height: 80, borderRadius: 4, background: 'linear-gradient(135deg, #0d47a1 0%, #1976d2 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px auto', color: 'white', fontSize: '35px' }}>📞</Box>
                                        <Typography variant="h5" sx={{ fontWeight: 800, mb: 1, color: '#0d47a1' }}>Телефон</Typography>
                                        <Typography variant="h6" sx={{ color: '#1976d2', fontWeight: 700, mb: 1 }}>{contactInfo?.phone}</Typography>
                                        <Typography variant="body2" sx={{ color: '#666', mb: 3 }}>{contactInfo?.workingHours}</Typography>
                                        <Button fullWidth variant="contained" sx={{ bgcolor: '#0d47a1', color: 'white', fontWeight: 'bold', py: 1.2 }} onClick={() => window.open(`tel:${contactInfo?.phone?.replace(/\s/g, '')}`, '_self')}>Повикај</Button>
                                    </Box>
                                </Grid>

                                <Grid item xs={12} sm={6} md={4} sx={{ display: 'flex' }}>
                                    <Box sx={{
                                        p: 4, width: '100%', bgcolor: '#ffffff', borderRadius: 6, textAlign: 'center',
                                        boxShadow: '0 20px 40px rgba(255, 126, 185, 0.1)', border: '1px solid #ff7eb940',
                                        transition: '0.3s', '&:hover': { transform: 'translateY(-10px)', borderColor: '#ff7eb9' }
                                    }}>
                                        <Box sx={{ width: 80, height: 80, borderRadius: 4, background: 'linear-gradient(135deg, #ff7eb9 0%, #ff4a97 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px auto', color: 'white', fontSize: '35px' }}>✉️</Box>
                                        <Typography variant="h5" sx={{ fontWeight: 800, mb: 1, color: '#0d47a1' }}>Е-маил</Typography>
                                        <Typography variant="h6" sx={{ color: '#ff7eb9', fontWeight: 700, mb: 3 }}>{contactInfo?.emailPrimary}</Typography>
                                        <Button fullWidth variant="contained" sx={{ bgcolor: '#ff7eb9', color: 'white', fontWeight: 'bold', py: 1.2 }} onClick={() => window.open(`mailto:${contactInfo?.emailPrimary}`)}>Пиши ни</Button>
                                    </Box>
                                </Grid>

                                <Grid item xs={12} sm={12} md={4} sx={{ display: 'flex' }}>
                                    <Box sx={{
                                        p: 4, width: '100%', bgcolor: '#ffffff', borderRadius: 6, textAlign: 'center',
                                        boxShadow: '0 20px 40px rgba(255, 209, 102, 0.1)', border: '1px solid #ffd16640',
                                        transition: '0.3s', '&:hover': { transform: 'translateY(-10px)', borderColor: '#ffd166' }
                                    }}>
                                        <Box sx={{ width: 80, height: 80, borderRadius: 4, background: 'linear-gradient(135deg, #ffd166 0%, #f7b733 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px auto', color: 'white', fontSize: '35px' }}>🌐</Box>
                                        <Typography variant="h5" sx={{ fontWeight: 800, mb: 2, color: '#0d47a1' }}>Следи не</Typography>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                            {/* Прв Instagram */}
                                            <Button variant="outlined" startIcon={<InstagramIcon />} sx={{ color: '#E4405F', borderColor: '#E4405F', fontWeight: 'bold' }} onClick={() => window.open(contactInfo?.instagramUrl, '_blank')}>Instagram 1</Button>

                                            {/* Втор Instagram (преку facebookUrl) */}
                                            <Button variant="outlined" startIcon={<InstagramIcon />} sx={{ color: '#833AB4', borderColor: '#833AB4', fontWeight: 'bold' }} onClick={() => window.open(contactInfo?.facebookUrl, '_blank')}>Instagram 2</Button>

                                            {/* LinkedIn */}
                                            <Button variant="outlined" startIcon={<LinkedInIcon />} sx={{ color: '#0A66C2', borderColor: '#0A66C2', fontWeight: 'bold' }} onClick={() => window.open(contactInfo?.linkedinUrl, '_blank')}>LinkedIn</Button>
                                        </Box>
                                    </Box>
                                </Grid>
                            </Grid>
                        )}
                    </Box>

                    {/* ===== LynxRise Labs Section ===== */}
                    <Box sx={{
                        my: 10, p: { xs: 4, md: 8 }, borderRadius: 8,
                        background: 'linear-gradient(135deg, #0b2d24 0%, #1b5e20 100%)', color: 'white', textAlign: 'center', position: 'relative', overflow: 'hidden'
                    }}>
                        <CodeIcon sx={{ position: 'absolute', top: -20, right: -20, fontSize: 150, opacity: 0.1, color: '#81c784' }} />
                        <Typography variant="overline" sx={{ letterSpacing: 3, color: '#81c784', fontWeight: 'bold' }}>SOFTWARE SOLUTIONS</Typography>
                        <Typography variant="h3" sx={{ fontWeight: '900', mt: 1, mb: 2, fontSize: { xs: '1.8rem', md: '2.5rem' } }}>Креирање на вашата дигитална иднина</Typography>
                        <Typography variant="h6" sx={{ opacity: 0.8, mb: 4, maxWidth: 700, mx: 'auto', fontWeight: 400 }}>
                            Оваа платформа е гордо развиена од <strong>LynxRise Labs</strong>. Потребни ви се модерни веб решенија? Ние сме тука да ги реализираме вашите идеи.
                        </Typography>
                        <Button variant="contained" startIcon={<LaunchIcon />} sx={{ bgcolor: '#81c784', color: '#0b2d24', fontWeight: '900', px: 6, py: 2, borderRadius: 4, '&:hover': { bgcolor: '#ffffff' } }} onClick={() => window.open('https://lynxriselabs.com/', '_blank')}>
                            Посетете ја LynxRise Labs
                        </Button>
                    </Box>

                </Container>
                <Footer />
            </Box>
        </ThemeProvider>
    );
};

export default HomePage;